import React from 'react';
import styled from 'styled-components';
import { FaDownload, FaArrowRight, FaGlobe, FaWifi, FaUsers } from 'react-icons/fa';
import { chatwithagentScreen, chatScreen, agentStoreScreen } from '../assets';

const HeroSection = styled.section`
  padding: 12rem 0 6rem;
  position: relative;
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 50%, rgba(var(--accent-rgb), 0.1) 0%, transparent 40%),
               radial-gradient(circle at 80% 20%, rgba(var(--accent-rgb), 0.1) 0%, transparent 40%);
  }
  
  @media (max-width: 768px) {
    padding: 10rem 0 6rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 992px) {
    flex-direction: column;
    text-align: center;
  }
`;

const HeroContent = styled.div`
  max-width: 540px;
  
  @media (max-width: 992px) {
    max-width: 100%;
    margin-bottom: 3rem;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  color: var(--white);
  margin-bottom: 1.5rem;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2.5rem;
  line-height: 1.6;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2.5rem;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  margin-bottom: 1rem;
  
  svg {
    margin-right: 0.75rem;
    color: var(--white);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 992px) {
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
  }
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: var(--accent-color);
  color: var(--white);
  font-weight: 600;
  padding: 0.875rem 1.75rem;
  border-radius: var(--border-radius);
  text-decoration: none;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  svg {
    margin-right: 0.5rem;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(var(--accent-rgb), 0.3);
    background-color: var(--accent-light);
  }
  
  @media (max-width: 480px) {
    width: 100%;
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  color: var(--white);
  font-weight: 600;
  padding: 0.875rem 1.75rem;
  border-radius: var(--border-radius);
  text-decoration: none;
  border: 2px solid rgba(var(--accent-rgb), 0.3);
  transition: transform 0.3s ease, border-color 0.3s ease, background-color 0.3s ease;
  
  svg {
    margin-left: 0.5rem;
  }
  
  &:hover {
    transform: translateY(-2px);
    border-color: var(--accent-color);
    background-color: rgba(var(--accent-rgb), 0.1);
  }
  
  @media (max-width: 480px) {
    width: 100%;
  }
`;

const PhonesContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  
  @media (max-width: 992px) {
    margin: 0 auto;
  }
`;

const PhoneWrapper = styled.div`
  position: relative;
  max-width: 220px;
  z-index: ${props => props.zIndex || 1};
  transform: ${props => props.transform || 'none'};
  margin: ${props => props.margin || '0'};
  
  @media (max-width: 992px) {
    max-width: 200px;
  }
  
  @media (max-width: 480px) {
    max-width: 180px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 20px;
    left: 20px;
    right: -20px;
    bottom: -20px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 30px;
    z-index: -1;
  }
`;

const PhoneMockup = styled.div`
  position: relative;
  border-radius: 30px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  border: 10px solid #333;
  transform: ${props => props.rotate ? `rotate(${props.rotate})` : 'none'};
  transition: transform 0.5s ease;
  
  &::before {
    content: '';
    display: block;
    position: absolute;
    width: 40%;
    height: 25px;
    background: #333;
    border-radius: 0 0 20px 20px;
    top: 0;
    left: 30%;
    z-index: 2;
  }
`;

const AppImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const Shape = styled.div`
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  z-index: 1;
  
  &.shape1 {
    width: 300px;
    height: 300px;
    top: -150px;
    left: -100px;
  }
  
  &.shape2 {
    width: 200px;
    height: 200px;
    bottom: -50px;
    right: 10%;
  }
`;

const HeroImageContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 800px;
  margin-left: -40px;
  perspective: 1000px;
  
  @media (max-width: 992px) {
    margin: 0 auto;
    transform: scale(0.9);
  }

  @media (max-width: 768px) {
    transform: scale(0.8);
  }
`;

const PhoneMockupWrapper = styled.div`
  position: relative;
  width: ${props => props.isMain ? '300px' : '240px'};
  margin: ${props => props.margin || '0'};
  transform: ${props => props.transform || 'none'};
  z-index: ${props => props.zIndex || 1};
  transition: all 0.5s ease;

  &:hover {
    transform: ${props => props.hoverTransform || 'none'};
  }
`;

const Hero = () => {
  return (
    <HeroSection id="home">
      <Shape className="shape1" />
      <Shape className="shape2" />
      <Container>
        <HeroContent>
          <HeroTitle>Your AI Companion, Always There for You</HeroTitle>
          <HeroSubtitle>
            One conversation away from the support you need.
          </HeroSubtitle>
          <FeatureList>
            <FeatureItem>
              <FaGlobe /> Supports major Indian languages
            </FeatureItem>
            <FeatureItem>
              <FaWifi /> Works in both online and offline modes
            </FeatureItem>
            
          </FeatureList>
          <ButtonGroup>
            <PrimaryButton href="#contact">
              <FaDownload /> Get Early Access
            </PrimaryButton>
            <SecondaryButton href="#features">
              Explore Features <FaArrowRight />
            </SecondaryButton>
          </ButtonGroup>
        </HeroContent>
        
        <HeroImageContainer>
          <PhoneMockupWrapper 
            transform="perspective(1000px) rotateY(25deg) translateX(40px)"
            zIndex={1}
            hoverTransform="perspective(1000px) rotateY(20deg) translateX(40px)"
            margin="0 -20px 0 0"
          >
            <PhoneMockup>
              <AppImage src={agentStoreScreen} alt="Hallow agent selection" />
            </PhoneMockup>
          </PhoneMockupWrapper>
          
          <PhoneMockupWrapper 
            isMain={true}
            zIndex={3}
            transform="translateY(-20px)"
            hoverTransform="translateY(-30px)"
          >
            <PhoneMockup>
              <AppImage src={chatwithagentScreen} alt="Hallow AI Assistant in action" />
            </PhoneMockup>
          </PhoneMockupWrapper>

          <PhoneMockupWrapper 
            transform="perspective(1000px) rotateY(-25deg) translateX(-40px)"
            zIndex={1}
            hoverTransform="perspective(1000px) rotateY(-20deg) translateX(-40px)"
            margin="0 0 0 -20px"
          >
            <PhoneMockup>
              <AppImage src={chatScreen} alt="Hallow chat interface" />
            </PhoneMockup>
          </PhoneMockupWrapper>
        </HeroImageContainer>
      </Container>
    </HeroSection>
  );
};

export default Hero; 